import { DAYS_OF_WEEK } from "@offstreetparking/domain/value";
import { DateTime } from "luxon";
import type { WizardParkingChoice } from "./PermitContextProvider/types";
import type { PermitTimeFormData } from "./components/ParkingTimeForm/permitTimeSchema";

type PermitInterval = PermitTimeFormData["choices"][number]["permitIntervals"][number];

const DATE_FORMAT = "yyyy-MM-dd";

const isLockedDate = (dateRange: WizardParkingChoice["time"]["startDate"]) =>
	!!dateRange.min && !!dateRange.max && dateRange.min === dateRange.max;

const findNextAllowedDate = (startDate: DateTime, allowedDays: readonly string[] | undefined) => {
	if (!allowedDays || allowedDays.length === 0) {
		return startDate;
	}

	let date = startDate;
	for (let i = 0; i < 7; i++) {
		const dayName = DAYS_OF_WEEK[date.weekday - 1];
		if (dayName && allowedDays.includes(dayName)) {
			return date;
		}
		date = date.plus({ days: 1 });
	}
	return startDate;
};

const resolveDefaultStartDate = (wizardSettings: WizardParkingChoice["time"], timezone: string) => {
	const { startDate } = wizardSettings;
	const now = DateTime.now().setZone(timezone).startOf("day");

	if (startDate.instant) {
		return now.toFormat(DATE_FORMAT);
	}

	if (isLockedDate(startDate)) {
		return startDate.min;
	}

	if (!startDate.enabled) {
		return undefined;
	}

	const minDate = startDate.min ? DateTime.fromISO(startDate.min, { zone: timezone }).startOf("day") : undefined;
	const candidate = minDate && minDate > now ? minDate : now;
	return findNextAllowedDate(candidate, startDate.allowedDays).toFormat(DATE_FORMAT);
};

export const makePermitIntervals = (
	wizardSettings: WizardParkingChoice["time"],
	timezone: string,
	currentIntervals?: PermitInterval[],
): PermitInterval[] => {
	// Set fixed intervals
	if (wizardSettings.intervals && wizardSettings.intervals.length > 0) {
		return wizardSettings.intervals.map((interval) => ({
			startDate: DateTime.fromISO(interval.startDateTimeISO).setZone(timezone).toFormat(DATE_FORMAT),
			startTime: DateTime.fromISO(interval.startDateTimeISO).setZone(timezone).toFormat("HH:mm:ss"),
			endDate: interval.endDateTimeISO
				? DateTime.fromISO(interval.endDateTimeISO).setZone(timezone).toFormat(DATE_FORMAT)
				: undefined,
			endTime: interval.endDateTimeISO
				? DateTime.fromISO(interval.endDateTimeISO).setZone(timezone).toFormat("HH:mm:ss")
				: undefined,
			schedules: interval.schedules,
		}));
	}

	let permitInterval: Partial<PermitInterval> = currentIntervals?.[0] ?? {};

	// Set instant start date and/or time independently from StartDateIsNowRule
	// and StartTimeIsNowRule.
	if (wizardSettings.startDate.instant || wizardSettings.startTime.instant) {
		const now = DateTime.now().setZone(timezone);
		permitInterval = {
			...permitInterval,
			...(wizardSettings.startDate.instant ? { startDate: now.toFormat(DATE_FORMAT) } : {}),
			...(wizardSettings.startTime.instant ? { startTime: now.toFormat("HH:mm:ss") } : {}),
		};
	}

	if (!permitInterval.startDate && wizardSettings.startTime.instant) {
		const startDate = resolveDefaultStartDate(wizardSettings, timezone);
		if (startDate) {
			permitInterval = {
				...permitInterval,
				startDate,
			};
		}
	}

	// Set fixed start date
	if (
		wizardSettings.startDate.min &&
		wizardSettings.startDate.max &&
		wizardSettings.startDate.min === wizardSettings.startDate.max
	) {
		permitInterval = {
			...permitInterval,
			startDate: wizardSettings.startDate.max,
		};
	}

	// Set fixed start time
	if (
		wizardSettings.startTime.min &&
		wizardSettings.startTime.max &&
		wizardSettings.startTime.min === wizardSettings.startTime.max
	) {
		permitInterval = {
			...permitInterval,
			startTime: wizardSettings.startTime.max,
		};
	}

	// Set fixed end date
	if (
		wizardSettings.endDate.min &&
		wizardSettings.endDate.max &&
		wizardSettings.endDate.min === wizardSettings.endDate.max
	) {
		permitInterval = {
			...permitInterval,
			endDate: wizardSettings.endDate.max,
		};
	}

	// Set fixed end time
	if (
		wizardSettings.endTime.min &&
		wizardSettings.endTime.max &&
		wizardSettings.endTime.min === wizardSettings.endTime.max
	) {
		permitInterval = {
			...permitInterval,
			endTime: wizardSettings.endTime.max,
		};
	}

	// duration rule
	if (
		wizardSettings.duration.min !== undefined &&
		wizardSettings.duration.max !== undefined &&
		wizardSettings.duration.min === wizardSettings.duration.max
	) {
		const startDateTime =
			permitInterval.startDate && permitInterval.startTime
				? DateTime.fromFormat(
						`${permitInterval.startDate} ${permitInterval.startTime}`,
						"yyyy-MM-dd HH:mm:ss",
						{
							zone: timezone,
						},
					)
				: undefined;

		if (startDateTime) {
			const endDateTime = startDateTime.plus({ minutes: wizardSettings.duration.max });
			let endTime = endDateTime.toFormat("HH:mm:ss");

			// When a fixed duration would push the end time past endTime.max (from ExpiryTimeBeforeTimeRule),
			// truncate to endTime.max — mirrors the backend TruncateSetDurationToFixedExpiryTimeRule
			if (wizardSettings.endTime.max) {
				const maxTimeDt = DateTime.fromFormat(wizardSettings.endTime.max, "HH:mm:ss");
				const endTimeDt = DateTime.fromFormat(endTime, "HH:mm:ss");
				if (endTimeDt > maxTimeDt) {
					endTime = wizardSettings.endTime.max;
				}
			}

			permitInterval = {
				...permitInterval,
				endDate: endDateTime.toFormat(DATE_FORMAT),
				endTime,
			};
		}
	}

	// expires in number of days and expires same day
	if (wizardSettings.duration.days !== undefined) {
		const startDateTime = permitInterval.startDate
			? DateTime.fromFormat(permitInterval.startDate, DATE_FORMAT)
			: undefined;

		if (startDateTime) {
			permitInterval = {
				...permitInterval,
				endDate: startDateTime.plus({ days: wizardSettings.duration.days }).toFormat(DATE_FORMAT),
			};
		}
	}

	if (Object.keys(permitInterval).length > 0) {
		return [permitInterval as PermitInterval];
	}

	return [];
};

export const makePermitIntervalsISO = (
	wizardSettings: WizardParkingChoice["time"],
	timezone: string,
	currentIntervals?: PermitInterval[],
) => {
	return makePermitIntervals(wizardSettings, timezone, currentIntervals).map((interval) => ({
		startDateTimeISO: DateTime.fromFormat(`${interval.startDate} ${interval.startTime}`, "yyyy-MM-dd HH:mm:ss", {
			zone: timezone,
		})
			.toUTC()
			.toISO()!,
		endDateTimeISO:
			interval.endDate && interval.endTime
				? DateTime.fromFormat(`${interval.endDate} ${interval.endTime}`, "yyyy-MM-dd HH:mm:ss", {
						zone: timezone,
					})
						.toUTC()
						.toISO()!
				: undefined,
		schedules: interval.schedules,
	}));
};
